import ApiGatewayFunction from "../../UserConfigs/ApiGatewayFunction.js";

/**
 * Requests a presigned upload URL, then PUTs the file directly to S3.
 *
 * Unlike uploadAsset (which just triggers a refetch afterward, since
 * assets have their own list endpoint), references travel with the
 * config blob — there's no list to refetch. So this returns the metadata
 * the caller needs to build the reference object itself and persist it
 * via updateBrandReferences.
 */
export async function uploadReference(
  config,
  { brandId, kind, file, currentReferenceCount },
  setIsLoading,
  setApiResponse
) {
  const payload = {
    request_type: "studio/references",
    request_sub_type: "reference_upload_url",
    client_id: config.clientId,
    brand_id: brandId,
    kind,
    content_type: file.type,
    file_size_bytes: file.size,
    current_reference_count: currentReferenceCount,
  };

  const response = await ApiGatewayFunction(
    payload,
    config.patternAPI,
    config.support_key,
    config.support_id,
    setIsLoading,
    setApiResponse,
    config.testingState
  );

  const uploadUrl = response?.data?.uploadUrl;
  const referenceId = response?.data?.referenceId;
  const key = response?.data?.key;
  const url = response?.data?.url;

  if (!uploadUrl || !referenceId) {
    throw new Error("No upload URL returned");
  }

  const putResponse = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!putResponse.ok) {
    throw new Error(`Upload failed (${putResponse.status})`);
  }

  return { referenceId, key, url, sizeBytes: file.size };
}

/**
 * Deletes the underlying S3 object. Does NOT touch brandConfig state —
 * the caller is responsible for removing the entry from `references` and
 * persisting via updateBrandReferences separately. (This split is the
 * accepted orphan-file gap we discussed — if the config save fails after
 * this succeeds, or vice versa, the two can drift.)
 */
export async function deleteReferenceFile(config, { brandId, key }, setIsLoading, setApiResponse) {
  const payload = {
    request_type: "studio/references",
    request_sub_type: "reference_delete",
    client_id: config.clientId,
    brand_id: brandId,
    key,
  };

  await ApiGatewayFunction(
    payload,
    config.patternAPI,
    config.support_key,
    config.support_id,
    setIsLoading,
    setApiResponse,
    config.testingState
  );
}